import { Component, EventEmitter, HostListener, input, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MessageService} from 'primeng/api';
import { FileUpload } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import {} from '@angular/common/http';
import { ProgressBar } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { SupervisorApiService } from '../../../core/api/services';
import { SupervisorHelperService } from '../../../supervisor/supervisor.helper.service';
import { AlertService } from '../../services/alert.service';
import { GeneralAttachmentModel } from '../../../core/api/models';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

interface ImagePointerPosition {
  x: number;
  y: number;
}

@Component({
  selector: 'app-attachment-view',
  templateUrl: './attachment-view.component.html',
  styleUrl: './attachment-view.component.css',
  standalone: false,
})
export class AttachmentViewComponent implements OnInit, OnDestroy  {
  
  @Input() attachment: GeneralAttachmentModel | null = null;
  @Input() doShowAttachmentView: boolean = false;
  @Input() canDelete: boolean = false;
  @Input() view: string = "";
  @Output() close = new EventEmitter<boolean>();

  doShowModal: boolean = false;
  files: File[] = [];
  totalSize : number = 0;
  totalSizePercent : number = 0;
  index: number | null = null;
  dragAreaClass: string = "dragarea"; 
  blobToSave: Blob | null = null; 
  uploadErrors: number = 0;
  isPDF: boolean = false;

  isLoading: boolean = false;
  isLoadingPDF: boolean = false;
  securePdfUrl: string | null = null;
  previewLoadFailed: boolean = false;
  private pdfSubscription: Subscription | null = null;
  minImageZoom: number = 1;
  maxImageZoom: number = 4;
  imageZoom: number = 1;
  imagePanX: number = 0;
  imagePanY: number = 0;
  isPanningImage: boolean = false;
  private panStartX: number = 0;
  private panStartY: number = 0;
  private panOriginX: number = 0;
  private panOriginY: number = 0;
  private activeImagePointers = new Map<number, ImagePointerPosition>();
  private pinchStartDistance: number = 0;
  private pinchStartZoom: number = 1;
  private pinchStartCenterX: number = 0;
  private pinchStartCenterY: number = 0;
  private pinchStartPanX: number = 0;
  private pinchStartPanY: number = 0;

  constructor(
    private supervisorApiService: SupervisorApiService,
    private supervisorHelperService: SupervisorHelperService,
    private alertService: AlertService,
    private http: HttpClient
  ) {}
  
  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['doShowAttachmentView']) {
      if (this.doShowAttachmentView == true){
        this.doShowModal = true;
      }
      else{
        this.doShowModal = false;
      }
    }
    if(changes['attachment']){
      this.pdfSubscription?.unsubscribe();
      this.isLoadingPDF = false;
      this.previewLoadFailed = false;
      // Clean up previous secure URL if it exists
      if (this.securePdfUrl) {
        URL.revokeObjectURL(this.securePdfUrl);
        this.securePdfUrl = null;
      }
      this.resetImageView();
      
      this.isPDF = this.attachment?.FileType?.toLowerCase().includes("application/pdf") ?? false;
      if(this.isPDF){
        if(this.attachment?.URL != null){
          this.isLoadingPDF = true;
          this.loadSecurePdf();
        }
      }
    }
  }

  getImageUrl(att: GeneralAttachmentModel | null) : string {
    if(att == null){
      return "";
    }
    var url = 'data:'+ att?.FileType +';base64,'+ att?.ImageThumbnail;
    return url;
  }

  get imageTransform(): string {
    return `translate(${this.imagePanX}px, ${this.imagePanY}px) scale(${this.imageZoom})`;
  }

  zoomIn(): void {
    this.setImageZoom(this.imageZoom + 0.25);
  }

  zoomOut(): void {
    this.setImageZoom(this.imageZoom - 0.25);
  }

  resetImageView(): void {
    this.imageZoom = this.minImageZoom;
    this.imagePanX = 0;
    this.imagePanY = 0;
    this.isPanningImage = false;
    this.activeImagePointers.clear();
  }

  onImageWheel(event: WheelEvent): void {
    event.preventDefault();
    this.setImageZoom(this.imageZoom + (event.deltaY < 0 ? 0.25 : -0.25));
  }

  startImagePan(event: PointerEvent): void {
    event.preventDefault();
    this.activeImagePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

    if (this.activeImagePointers.size >= 2) {
      this.startImagePinch();
      return;
    }

    this.startSinglePointerPan(event.clientX, event.clientY);
  }

  panImage(event: PointerEvent): void {
    if (!this.activeImagePointers.has(event.pointerId)) {
      return;
    }

    event.preventDefault();
    this.activeImagePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.activeImagePointers.size >= 2) {
      this.updateImagePinch();
      return;
    }

    if (!this.isPanningImage) {
      return;
    }

    this.imagePanX = this.panOriginX + event.clientX - this.panStartX;
    this.imagePanY = this.panOriginY + event.clientY - this.panStartY;
  }

  endImagePan(event?: PointerEvent): void {
    if (event) {
      this.activeImagePointers.delete(event.pointerId);
      const currentTarget = event.currentTarget as HTMLElement | null;
      if (currentTarget?.hasPointerCapture(event.pointerId)) {
        currentTarget.releasePointerCapture(event.pointerId);
      }
    } else {
      this.activeImagePointers.clear();
    }

    if (this.activeImagePointers.size >= 2) {
      this.startImagePinch();
      return;
    }

    if (this.activeImagePointers.size === 1) {
      const remainingPointer = Array.from(this.activeImagePointers.values())[0];
      this.startSinglePointerPan(remainingPointer.x, remainingPointer.y);
      return;
    }

    this.isPanningImage = false;
  }

  private setImageZoom(zoom: number): void {
    this.imageZoom = Math.min(this.maxImageZoom, Math.max(this.minImageZoom, zoom));

    if (this.imageZoom === this.minImageZoom) {
      this.imagePanX = 0;
      this.imagePanY = 0;
      this.isPanningImage = false;
    }
  }

  private startSinglePointerPan(clientX: number, clientY: number): void {
    if (this.imageZoom <= this.minImageZoom) {
      this.isPanningImage = false;
      return;
    }

    this.isPanningImage = true;
    this.panStartX = clientX;
    this.panStartY = clientY;
    this.panOriginX = this.imagePanX;
    this.panOriginY = this.imagePanY;
  }

  private startImagePinch(): void {
    const [firstPointer, secondPointer] = Array.from(this.activeImagePointers.values());

    this.isPanningImage = false;
    this.pinchStartDistance = Math.max(this.getPointerDistance(firstPointer, secondPointer), 1);
    this.pinchStartZoom = this.imageZoom;
    this.pinchStartCenterX = (firstPointer.x + secondPointer.x) / 2;
    this.pinchStartCenterY = (firstPointer.y + secondPointer.y) / 2;
    this.pinchStartPanX = this.imagePanX;
    this.pinchStartPanY = this.imagePanY;
  }

  private updateImagePinch(): void {
    const [firstPointer, secondPointer] = Array.from(this.activeImagePointers.values());
    const currentDistance = this.getPointerDistance(firstPointer, secondPointer);
    const currentCenterX = (firstPointer.x + secondPointer.x) / 2;
    const currentCenterY = (firstPointer.y + secondPointer.y) / 2;

    this.setImageZoom(this.pinchStartZoom * (currentDistance / this.pinchStartDistance));

    if (this.imageZoom > this.minImageZoom) {
      this.imagePanX = this.pinchStartPanX + currentCenterX - this.pinchStartCenterX;
      this.imagePanY = this.pinchStartPanY + currentCenterY - this.pinchStartCenterY;
    }
  }

  private getPointerDistance(firstPointer: ImagePointerPosition, secondPointer: ImagePointerPosition): number {
    return Math.hypot(firstPointer.x - secondPointer.x, firstPointer.y - secondPointer.y);
  }

  hideModal(): void {
    this.pdfSubscription?.unsubscribe();
    this.isLoadingPDF = false;
    // Clean up the secure PDF URL when closing the modal
    if (this.securePdfUrl) {
      URL.revokeObjectURL(this.securePdfUrl);
      this.securePdfUrl = null;
    }
    this.close.emit();
  }

  downloadFile() : void {
    if (this.isPDF && this.securePdfUrl) {
      // For PDFs, use the secure URL
      window.open(this.securePdfUrl, "_blank");
    } else {
      // For other files, use the original URL
      window.open(this.attachment?.URL ?? "", "_blank");
    }
  }

  deleteFile(): void {
    if(this.view == "patientInstructionAttachment"){
      this.deletePatientInstructionAttachmentFile();
    }
    else if (this.view == "targetAttachment"){
      this.deleteTargetInstructionAttachmentFile();
    }
  }

  deletePatientInstructionAttachmentFile(){
    this.alertService.confirm("Confirm Deletion", "Are you sure you want to delete this file?")
    .subscribe(result => {  
      if(result){
        if (this.attachment?.ID != null) {
          this.isLoading = true;
          this.supervisorApiService.DeletePatientInstructionAttachment({
            attachmentID: this.attachment?.ID ,
          })
          .subscribe({
            next: (response) => {
              this.alertService.success('File ' + this.attachment?.FileName + ' saved successfully');
              this.isLoading = false;
              this.hideModal();
            },
            error: (error) => {
              this.alertService.error('Error deleting file: ' + this.attachment?.FileName);
              console.log(error);
              this.isLoading = false;
            }
          }); 
        }
        else {
          this.alertService.error("Attachment was not found.");
        }
      }
    });
  }

  deleteTargetInstructionAttachmentFile(){
    this.alertService.confirm("Confirm Deletion", "Are you sure you want to delete this file?")
    .subscribe(result => {  
      if(result){
        if (this.attachment?.ID != null) {
          this.isLoading = true;
          this.supervisorApiService.DeleteTargetAttachment({
            attachmentID: this.attachment?.ID ,
          })
          .subscribe({
            next: (response) => {
              this.alertService.success('File ' + this.attachment?.FileName + ' saved successfully');
              this.isLoading = false;
              this.hideModal();
            },
            error: (error) => {
              this.alertService.error('Error deleting file: ' + this.attachment?.FileName);
              console.log(error);
              this.isLoading = false;
            }
          }); 
        }
        else {
          this.alertService.error("Attachment was not found.");
        }
      }
    });
  }


  private loadSecurePdf(): void {
    if (!this.attachment?.URL) {
      this.isLoadingPDF = false;
      return;
    }

    // Fetch the PDF as a blob to handle authentication
    this.pdfSubscription = this.http.get(this.attachment.URL, { responseType: 'blob' })
      .subscribe({
        next: (blob) => {
          // Create a secure object URL from the blob
          this.securePdfUrl = URL.createObjectURL(blob);
          this.isLoadingPDF = false;
        },
        error: (error) => {
          console.error('Error fetching PDF: ', error);
          this.previewLoadFailed = true;
          this.alertService.error('Error loading PDF. Please try again.');
          this.isLoadingPDF = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.pdfSubscription?.unsubscribe();
    // Clean up the object URL to prevent memory leaks
    if (this.securePdfUrl) {
      URL.revokeObjectURL(this.securePdfUrl);
    }
  }

}
